import {MdHinges} from "./MdHinges";
import {Opening} from "../../../../../../api/Openings";
import {HardwareGroupWithContent, HardwareItem} from "../../../../../../api/Hardware";
import {UnitSystem} from "../../../../../../api/Projects";
import {MdFrameSpec} from "../frame/MdFrameSpec";
import {MdDoorSpec} from "../door/MdDoorSpec";
import {FrmVar} from "../../drawfrm/FrmVar";
import {EventEmitter2} from "../../util/EventEmitter2";
import {hasFramePrep, isCosmetic, isHinge, placeableOnDoor} from "../../scenes/hardware/hardwarePreps";
import {inches} from "../../const";
import {getSwingSide} from "../../scenes/frameProfile/Helpers";
import {ElevationApp} from "../../ElevationApp";
import {serFrmVar, restoreFrmVar} from "../../serialize/frmVar";
import {HardwareDesign, LockPreps, SerAnchor, SerPrepAnchors} from "../../../../../../api/elevationTool/HardwareElevationData";
import {SerFrmVar} from "../../../../../../api/elevationTool/shared";
import {prepConfigDrawingUnits} from "../../util/DrawingUnits";
import {isDeadboltItem} from "../../../hardware/prepCanvas/PrepManager";
import {AnchorKind, AnchorValues, anchorFromPosition, anchorPosition, isPrepKind, leafGeometry, linkedPosition, PREP_KINDS, PrepKind, prepLinkFor, ruleForItem, RuleLeaf, standardAnchor, standardFrameYOn, standardPositionOn} from "./MdHardwareRules";
import {Subscription} from "nate-react-api-helpers";

// standard door lock/deadbolt heights above the door bottom + the standard backset (edge -> bore
// centre; the crosshair x/y are to the centre of the hole, so the backset is the placement x itself).
// Shared by the seeded primaries (ensurePrimaries) and the prep projection (toLockPreps) so the "at
// standard = no override" test can't drift from the seed.
export const CENTER_LOCK_ABOVE_BOTTOM = (39 + 9 / 16) * inches;
export const DEADBOLT_ABOVE_BOTTOM = (47 + 1 / 4) * inches;
export const STANDARD_BACKSET = (2 + 3 / 4) * inches;
const POSITION_EPS = inches / 64; // 1/64" — below this a placement counts as at-standard

export class MdHardware {

    hinges: MdHinges = new MdHinges();

    // hardware items the user has placed on the drawing. Each entry is one
    // crosshair on either the door leaf ("door") or the frame perimeter ("frame");
    // an item with both preps gets one of each. A product missing from this list
    // hasn't been spec'd yet. In-memory only (like the rest of the tool) — cleared
    // whenever MdHardware is rebuilt (ElevationApp.resetFrameSpec).
    list: MdHardwarePlacement[] = [];
    listChanged = new EventEmitter2<string>("", "hardware.listChanged");

    // the opening's hardware group items (non-archived), for the category rules.
    private items: HardwareItem[] = [];

    // the prep anchors per leaf: where the 161 (cylindrical lock) and DB (deadbolt) holes
    // sit, each as a height above the finished floor and a backset from the strike edge.
    // Every door placement linked to a prep (linkedTo) derives its position from it, so
    // the exit device, its trim and the primary handle sit on the 161 and keep their
    // measurements when the leaf resizes. A var left unmodified means the standard location
    // (see standardAnchor); dragging any linked placement, or editing the prep layer's
    // dimension, moves the anchor (userSet) and with it every placement on it.
    prepAnchors: Record<"active" | "inactive", Record<AnchorKind, Anchor>> = {
        active: makeAnchors("active"),
        inactive: makeAnchors("inactive"),
    };

    // the frame spec the leaf geometry (hinge side) reads; set with doorSpec by attach.
    private frameSpec: MdFrameSpec | null = null;

    // the subscriptions that re-place the linked placements when the anchors or the leaf
    // geometry change; rebuilt by attach.
    private geometrySubs: Subscription[] = [];

    // the subscriptions that re-place the edge items when the hinges move (an edge item
    // sits at its jamb placement's standard height, which hangs off a hinge); rebuilt by
    // watchHinges.
    private hingeSubs: (() => void)[] = [];

    constructor() {
        this.listChanged.subscribe(() => this.applyPositions());
    }

    // bind the specs the leaf geometry reads and keep the linked placements on their
    // anchors from here on.
    private attach(frameSpec: MdFrameSpec, doorSpec: MdDoorSpec) {
        this.frameSpec = frameSpec;
        this.doorSpec = doorSpec;

        for(const sub of this.geometrySubs) sub.cancel();
        const apply = () => this.applyPositions();
        const anchorVars = (["active", "inactive"] as const).flatMap(side =>
            (["161", "db"] as const).flatMap(kind => [this.prepAnchors[side][kind].aboveFloor, this.prepAnchors[side][kind].fromStrike]));
        this.geometrySubs = [
            doorSpec.actualHeight, doorSpec.undercut, doorSpec.activeDoorWidth, doorSpec.inActiveDoorWidth,
            ...anchorVars,
        ].map(v => v.subscribe(apply));
    }

    private setItems(hardwareGroup: HardwareGroupWithContent | null) {
        this.items = (hardwareGroup?.hardware ?? []).filter(i => !i.archived);
    }

    // the door geometry the lock/deadbolt/backset projection reads (toLockPreps): the placements' x/y
    // are offsets within the door rect (height = actualHeight, the cut door), and cld/dbd back out the
    // undercut. Set by fromOpening/fromDesign; null only for a bare `new MdHardware()`.
    private doorSpec: MdDoorSpec | null = null;

    // whether this product already has at least one placement.
    has(productId: number): boolean {
        return this.list.some(p => p.productId === productId);
    }

    // place a hardware item: a door crosshair (per its door prep, or for surface hardware
    // the drawing always locates, see placeableOnDoor) and/or a frame crosshair per its
    // frame prep. No-op if already added. A door placement whose rule gives it a standard
    // position starts there; every other position is seeded lazily (centered) by the
    // crosshair Thing on first draw.
    add(app: ElevationApp, item: HardwareItem) {
        if(this.has(item.product)) return;

        let added = false;
        if(placeableOnDoor(item)) {
            if(item.activeQty > 0) {
                this.list.push(makePlacement(item.product, "door", "active"));
                added = true;
            }

            if(item.inactiveQty > 0) {
                this.list.push(makePlacement(item.product, "door", "inactive"));
                added = true;
            }

        }
        if(hasFramePrep(item)) {
            this.list.push(makePlacement(item.product, "frame", null));
            added = true;
        }

        if(!added) return;

        const doorPlacements = this.list.filter(p => p.productId === item.product && p.target === "door");

        // start a rule-placed item linked to its prep (exit hardware and pulls on the 161, a
        // closer in the closer band); every door placement is then put at its position by
        // applyPositions (on listChanged).
        for(const p of doorPlacements) {
            const link = this.linkFor(p);
            if(link) p.linkedTo = link;
        }

        this.listChanged.emit("add");
    }

    // place every item of the group that the drawing locates (see add): every hardware item
    // is on the drawing, there is no per-item opt-in. Runs after ensurePrimaries so the
    // primary handle / deadbolt keep their seeded placements. Hinges are placed by MdHinges.
    private ensureAll(app: ElevationApp, hardwareGroup: HardwareGroupWithContent | null) {
        for(const item of hardwareGroup?.hardware ?? []) {
            if(item.archived || isHinge(item)) continue;
            this.add(app, item);
        }
    }

    // remove every placement (door + frame) for a product.
    remove(productId: number) {
        const before = this.list.length;
        this.list = this.list.filter(p => p.productId !== productId);
        if(this.list.length !== before) this.listChanged.emit("remove");
    }

    // ---- prep links ----------------------------------------------------------

    // the leaf a door placement sits on. Standard geometry (no hinge side, no undercut)
    // before attach.
    leafFor(side: "active" | "inactive"): RuleLeaf {
        if(!this.frameSpec || !this.doorSpec) return {width: 0, height: 0, undercut: 0, hingeSide: side === "active" ? "left" : "right"};
        return leafGeometry(this.frameSpec, this.doorSpec, side);
    }

    // a leaf's 161 / DB anchor as measurements: a modified var as set, else the standard.
    anchor(side: "active" | "inactive", kind: AnchorKind): AnchorValues {
        const anchor = this.prepAnchors[side][kind];
        const std = standardAnchor(kind, this.leafFor(side));
        return {
            aboveFloor: anchor.aboveFloor.isModified() ? anchor.aboveFloor.get() : std.aboveFloor,
            fromStrike: anchor.fromStrike.isModified() ? anchor.fromStrike.get() : std.fromStrike,
        };
    }

    // the hub position (rect offsets) of a leaf's 161 / DB anchor.
    anchorPosition(side: "active" | "inactive", kind: AnchorKind): {x: number; y: number} {
        return anchorPosition(this.leafFor(side), this.anchor(side, kind));
    }

    // move a leaf's anchor so its hole sits at `pos` (rect offsets); every placement linked
    // to it follows (see attach).
    moveAnchor(side: "active" | "inactive", kind: AnchorKind, pos: {x: number; y: number}) {
        const values = anchorFromPosition(this.leafFor(side), pos);
        const anchor = this.prepAnchors[side][kind];
        anchor.aboveFloor.userSet(values.aboveFloor, {debug: `hardware.${kind}.${side}.aboveFloor`});
        anchor.fromStrike.userSet(values.fromStrike, {debug: `hardware.${kind}.${side}.fromStrike`});
    }

    // put a leaf's anchor back at its standard location.
    resetAnchor(side: "active" | "inactive", kind: AnchorKind) {
        const anchor = this.prepAnchors[side][kind];
        if(anchor.aboveFloor.isModified()) anchor.aboveFloor.userClearModified();
        if(anchor.fromStrike.isModified()) anchor.fromStrike.userClearModified();
    }

    // the prep a placement links to by rule (see prepLinkFor), or null when it has none.
    linkFor(p: MdHardwarePlacement): PrepKind | null {
        return prepLinkFor(this.itemOf(p), p);
    }

    isLinked(p: MdHardwarePlacement): boolean {
        return !!p.linkedTo;
    }

    canLink(p: MdHardwarePlacement): boolean {
        return !!this.linkFor(p);
    }

    // the door placements on a leaf that sit on a prep.
    linkedTo(side: "active" | "inactive", kind: PrepKind): MdHardwarePlacement[] {
        return this.list.filter(p => p.target === "door" && p.side === side && p.linkedTo === kind);
    }

    // the preps a leaf has a placement on, for the prep layer.
    prepsUsed(side: "active" | "inactive"): PrepKind[] {
        return PREP_KINDS.filter(kind => this.linkedTo(side, kind).length > 0);
    }

    // link a placement to the prep its rule names; a no-op for one with none.
    link(p: MdHardwarePlacement) {
        const link = this.linkFor(p);
        if(!link) return;
        this.setLinked(p, link);
        this.listChanged.emit("link");
    }

    // free a placement from its prep; it stays where it is.
    unlink(p: MdHardwarePlacement) {
        p.linkedTo = null;
        this.listChanged.emit("unlink");
    }

    // link a placement to a prep and put it there.
    private setLinked(p: MdHardwarePlacement, link: PrepKind) {
        p.linkedTo = link;
        this.place(p);
    }

    // put every door placement where it belongs: on its prep, else at its standard
    // position. Door hardware is never dragged; it only moves with the prep it sits on
    // or with the leaf.
    private applyPositions() {
        if(!this.frameSpec || !this.doorSpec) return;
        for(const p of this.list) {
            if(p.target === "door") this.place(p);
        }
    }

    // set a door placement's x/y from its prep / standard position; a no-op when already
    // there (set() emits unconditionally, and the x/y subscribers redraw). An edge item
    // (a power transfer) sits at the height its jamb placement takes by rule, so the two
    // line up across the door edge and the jamb; the hinges and the leaf share the same
    // vertical datum (the top of the leaf / jamb).
    private place(p: MdHardwarePlacement) {
        const leaf = this.leafFor(p.side);
        const item = this.itemOf(p);
        const anchor = (kind: AnchorKind) => this.anchor(p.side, kind);

        const edgeY = item && ruleForItem(item)?.edgeOnly && this.frameSpec
            ? standardFrameYOn(item, this.frameSpec.nominalHeight.get(), this.hinges)
            : null;
        const pos = edgeY !== null ? {x: leaf.width / 2, y: edgeY}
            : p.linkedTo ? linkedPosition(leaf, p.linkedTo, item, anchor)
            : standardPositionOn(leaf, p, item, anchor);
        if(p.x.get() !== pos.x) p.x.set(pos.x, {debug: `${p.id}.place`});
        if(p.y.get() !== pos.y) p.y.set(pos.y, {debug: `${p.id}.place`});
    }

    // follow the hinges: their list and each hinge's height re-place the edge items.
    private watchHinges() {
        for(const unsub of this.hingeSubs) unsub();
        const apply = () => this.applyPositions();
        this.hingeSubs = [
            this.hinges.listChanged.subscribe(() => {
                this.watchHinges();
                apply();
            }),
            ...this.hinges.list.map(h => {
                const sub = h.offsetTop.subscribe(apply);
                return () => sub.cancel();
            }),
        ];
    }

    // store an intended position for a door placement: one on the 161 / DB moves that
    // anchor; any other door placement is fixed (see applyPositions), so this is a no-op.
    setDoorPosition(p: MdHardwarePlacement, pos: {x: number; y: number}) {
        if(p.linkedTo === "161" || p.linkedTo === "db") this.moveAnchor(p.side, p.linkedTo, pos);
    }

    private itemOf(p: MdHardwarePlacement): HardwareItem | null {
        return this.items.find(i => i.product === p.productId) ?? null;
    }

    static fromOpening(app: ElevationApp, opening: Opening, hardwareGroup: HardwareGroupWithContent | null, unitSystem: UnitSystem, frameSpec: MdFrameSpec, doorSpec: MdDoorSpec) {
        const hardware = new MdHardware();
        hardware.attach(frameSpec, doorSpec);
        hardware.setItems(hardwareGroup);

        hardware.hinges = MdHinges.fromOpening(opening, hardwareGroup, unitSystem, frameSpec, doorSpec);
        hardware.watchHinges();

        // layer the persisted hinge config (custom spacing/count + per-location product
        // picks) on top of the height-derived auto defaults seeded above.
        hardware.hinges.applyGroupConfig(hardwareGroup);

        hardware.ensurePrimaries(app, hardwareGroup, doorSpec);
        hardware.ensureAll(app, hardwareGroup);
        // migrate any legacy lock/deadbolt/backset prep overrides onto the seeded placements so they
        // survive the switch to elevation-owned preps.
        hardware.applyGroupLockConfig(hardwareGroup, doorSpec, frameSpec);

        return hardware;
    }

    // seed the door lockset/deadbolt/backset placements from the group's legacy prep_config_json so a
    // migrated opening's overrides survive (the inverse of toLockPreps). Resolves each door height from
    // whichever key the legacy prep stored — the door value, its *Star twin, or the frame value/twin
    // converted door-ward via the cut relationship — plus the backset. A lock with no stored override
    // keeps its ensurePrimaries default. Runs only from fromOpening (a stored elevation already carries
    // the positions).
    private applyGroupLockConfig(hardwareGroup: HardwareGroupWithContent | null, doorSpec: MdDoorSpec, frameSpec: MdFrameSpec) {
        if(!hardwareGroup) return;

        // apply the (single) legacy override to every leaf's placement — the leaves conventionally share
        // the height/backset, and seedBacksetFromPrep resolves each leaf's own edge.
        for(const p of this.doorPlacements("handle")) {
            this.seedDoorLockHeight(p, hardwareGroup, doorSpec, frameSpec, "cld", "cldStar", "clf", "clfStar");
        }
        for(const p of this.doorPlacements("lock")) {
            this.seedDoorLockHeight(p, hardwareGroup, doorSpec, frameSpec, "dbd", "dbdStar", "dbf", "dbfStar");
        }

        const backsetTargets = this.doorPlacements("handle");
        for(const p of (backsetTargets.length > 0 ? backsetTargets : this.doorPlacements("lock"))) {
            this.seedBacksetFromPrep(p, hardwareGroup, doorSpec);
        }
    }

    // set a door placement's y from the resolved door lock height. Inverse of verticalPrep (see
    // toLockPreps).
    private seedDoorLockHeight(p: MdHardwarePlacement | null, hardwareGroup: HardwareGroupWithContent, doorSpec: MdDoorSpec, frameSpec: MdFrameSpec, door: string, doorStar: string, frame: string, frameStar: string) {
        if(!p) return;

        const value = this.resolveDoorLockHeight(hardwareGroup, doorSpec, frameSpec, door, doorStar, frame, frameStar);
        if(value === null) return; // no stored override — keep the seeded default

        this.setDoorPosition(p, {x: p.x.get(), y: doorSpec.actualHeight.get() - value - doorSpec.undercut.get()});
    }

    // resolve a door lock height (cld/dbd, drawing units) from the stored overrides, preferring the door
    // key, then its *Star twin (cld = actualHeight - cldStar), then the frame key or its twin converted
    // door-ward via the cut relationship (cld = clf + underCutFrame - 3/4" - underCutDoor; clf =
    // innerFrameHeight - clfStar). Null when nothing is stored.
    private resolveDoorLockHeight(hardwareGroup: HardwareGroupWithContent, doorSpec: MdDoorSpec, frameSpec: MdFrameSpec, door: string, doorStar: string, frame: string, frameStar: string): number | null {
        const actualHeight = doorSpec.actualHeight.get();

        const d = prepConfigDrawingUnits(hardwareGroup, door);
        if(d !== null) return d;
        const ds = prepConfigDrawingUnits(hardwareGroup, doorStar);
        if(ds !== null) return actualHeight - ds;

        const underCutFrame = frameSpec.undercutFrame.get();
        const frameToDoor = (clf: number) => clf + underCutFrame - (3 / 4) * inches - doorSpec.undercut.get();

        const f = prepConfigDrawingUnits(hardwareGroup, frame);
        if(f !== null) return frameToDoor(f);
        const fs = prepConfigDrawingUnits(hardwareGroup, frameStar);
        if(fs !== null) return frameToDoor((frameSpec.nominalHeight.get() - underCutFrame) - fs);

        return null;
    }

    // set a door placement's x from the stored backset override (inverse of backsetPrep).
    private seedBacksetFromPrep(p: MdHardwarePlacement | null, hardwareGroup: HardwareGroupWithContent, doorSpec: MdDoorSpec) {
        if(!p) return;

        const bs = prepConfigDrawingUnits(hardwareGroup, "bs");
        if(bs === null) return;

        // the crosshair x is to the bore centre, so the backset is the from-edge distance directly.
        const doorWidth = p.side === "inactive" ? doorSpec.inActiveDoorWidth.get() : doorSpec.activeDoorWidth.get();
        this.setDoorPosition(p, {x: p.alignToX === "left" ? bs : doorWidth - bs, y: p.y.get()});
    }

    // auto-place the primary handle and lock (as door crosshairs) for the group's picks,
    // so an opening shows them without the user adding them by hand — skipping any already
    // placed. Their x/y are seeded to the legacy default positions and left tracking the
    // rect (like any placement) until the user drags them. Returns what it added.
    private ensurePrimaries(app: ElevationApp, hardwareGroup: HardwareGroupWithContent | null, doorSpec: MdDoorSpec): AddedPrimary[] {
        if(!hardwareGroup) return [];

        const added: AddedPrimary[] = [];

        // the center lock (cld) — the main latching lock, always present. Placed at the center-lock
        // height and drawn as the "handle" glyph.
        const centerLock = getCenterLock(hardwareGroup.hardware);
        if(centerLock && this.seedPrimary(app, doorSpec, centerLock, "handle", "primaryHandle", CENTER_LOCK_ABOVE_BOTTOM)) {
            added.push({productId: centerLock.product, kind: "handle"});
        }

        // the deadbolt (dbd) — only when the group has one, matching legacy hasDeadbolt (isDeadboltItem).
        // Placed at the deadbolt height and drawn as the "lock" (deadbolt) glyph.
        const isPair = app.frameSpec.isPair;
        const deadbolt = hardwareGroup.hardware.find(i => !i.archived && isDeadboltItem(i, isPair)) ?? null;
        if(deadbolt && this.seedPrimary(app, doorSpec, deadbolt, "lock", "primaryLock", DEADBOLT_ABOVE_BOTTOM)) {
            added.push({productId: deadbolt.product, kind: "lock"});
        }

        return added;
    }

    // seed the primary handle/lock placement for each leaf the product preps (activeQty / inactiveQty),
    // so a pair with hardware on both leaves gets an independent placement per leaf — each positioned on
    // its own leaf's lock stile (getSwingSide is per-leaf). The handle is linked to the leaf's 161 and
    // the deadbolt to its DB. Skips a leaf already placed. Returns whether it added anything. The active
    // leaf keeps the legacy id (idBase) so a stored design round-trips.
    private seedPrimary(app: ElevationApp, doorSpec: MdDoorSpec, product: HardwareItem, kind: "handle" | "lock", idBase: string, aboveBottom: number): boolean {
        let added = false;

        const seed = (active: boolean, side: "active" | "inactive") => {
            if(this.hasDoorSide(product.product, side)) return;

            // handle sits on the lock stile — the edge opposite the hinges — of this leaf.
            const isLeft = getSwingSide(app, active).side === "right";
            const doorWidth = active ? doorSpec.activeDoorWidth.get() : doorSpec.inActiveDoorWidth.get();
            const id = side === "active" ? idBase : `${idBase}:inactive`;

            const p: MdHardwarePlacement = {
                id,
                kind,
                productId: product.product,
                target: "door",
                side,
                alignToX: isLeft ? "left" : "right",
                alignToY: "bottom",
                x: new FrmVar(`hardware.${id}.x`, isLeft ? STANDARD_BACKSET : doorWidth - STANDARD_BACKSET).overrideIsModified(true),
                y: new FrmVar(`hardware.${id}.y`, doorSpec.actualHeight.get() - aboveBottom).overrideIsModified(true),
            };
            p.linkedTo = kind === "handle" ? "161" : "db";
            this.place(p);
            this.list.push(p);
            added = true;
        };

        if(product.activeQty > 0) seed(true, "active");
        if(product.inactiveQty > 0) seed(false, "inactive");

        return added;
    }

    // whether this product already has a door placement on the given leaf.
    private hasDoorSide(productId: number, side: "active" | "inactive"): boolean {
        return this.list.some(p => p.productId === productId && p.target === "door" && p.side === side);
    }

    // reconcile a design loaded from a stored elevation against the opening's current
    // hardware group. Hardware elevations are copy-not-assign, so each opening owns its own
    // copy; but the group's product set drifts as the hardware schedule is edited, so on
    // load: drop placements (and hinge product overrides) whose product is no longer in the
    // group, and auto-place any now-missing primary handle/lock. Returns a report for the
    // review dialog; the caller marks the elevation dirty so the cleanup persists on save.
    reconcileToGroup(app: ElevationApp, hardwareGroup: HardwareGroupWithContent | null, doorSpec: MdDoorSpec): HardwareReconcileReport {
        this.setItems(hardwareGroup);
        const valid = new Set(this.items.map(i => i.product));

        const droppedPlacements: DroppedPlacement[] = [];
        const kept: MdHardwarePlacement[] = [];
        for(const p of this.list) {
            if(valid.has(p.productId)) kept.push(p);
            else droppedPlacements.push({productId: p.productId, kind: p.kind, target: p.target});
        }
        this.list = kept;

        const addedPrimaries = this.ensurePrimaries(app, hardwareGroup, doorSpec);
        this.ensureAll(app, hardwareGroup);

        if(droppedPlacements.length > 0 || addedPrimaries.length > 0) this.listChanged.emit("reconcileToGroup");

        const droppedHingeOverrides = this.hinges.reconcileToGroup(hardwareGroup?.hardware ?? []);

        return {
            changed: droppedPlacements.length > 0 || addedPrimaries.length > 0 || droppedHingeOverrides.length > 0,
            droppedPlacements,
            addedPrimaries,
            droppedHingeOverrides,
        };
    }

    // serialize the hardware Design (placements + hinges).
    toDesign(): HardwareDesign {
        return {
            list: this.list.map(p => ({
                id: p.id,
                kind: p.kind,
                target: p.target,
                side: p.side,
                productId: p.productId,
                stickId: p.stickId,
                alignToX: p.alignToX,
                alignToY: p.alignToY,
                ...(p.linkedTo ? {linkedTo: p.linkedTo} : {}),
                x: serFrmVar(p.x),
                y: serFrmVar(p.y),
            })),
            prepAnchors: {
                active: serPrepAnchors(this.prepAnchors.active),
                inactive: serPrepAnchors(this.prepAnchors.inactive),
            },
            hinges: this.hinges.toDesign(),
            locks: this.toLockPreps(),
        };
    }

    // project the designated door lockset/deadbolt placements into the prep-convention overrides the
    // product calc consumes (cld/dbd/bs). Each is emitted as mod only when the placement sits away from
    // its standard position; a standard (or missing) placement stays unmod so the server keeps the
    // calc's default + derives the frame twins (clf/dbf) + *Star values itself. (isModified() can't be
    // the signal — ensurePrimaries seeds the primaries with overrideIsModified(true).)
    //
    // Vertical: a door placement's y is measured from the top of the door rect (height = actualHeight,
    // the cut door ≈ the prep exactDoorHeight), so cld/dbd = (actualHeight - y) - undercut — which
    // reproduces the seeded defaults. Roles map by kind: "handle" -> center lock, "lock" -> deadbolt
    // (matching ensurePrimaries' seeded heights).
    //
    // The deadbolt (which product, and whether present) is decided by isDeadboltItem in ensurePrimaries,
    // so a "lock" placement only exists when the group has a deadbolt.
    private toLockPreps(): LockPreps {
        const centerLock = this.doorPlacement("handle");
        const deadbolt = this.doorPlacement("lock");

        return {
            centerLockDoor: this.verticalPrep(centerLock, CENTER_LOCK_ABOVE_BOTTOM),
            deadboltDoor: this.verticalPrep(deadbolt, DEADBOLT_ABOVE_BOTTOM),
            backset: this.backsetPrep(centerLock ?? deadbolt),
        };
    }

    // the strike centres on the strike jamb, from the top of the jamb (the hinges' datum):
    // the lock strike from the centre-lock placement, the deadbolt strike from the deadbolt
    // placement. Only the placements present are listed.
    strikeCenters(): {kind: "lock" | "deadbolt"; y: number}[] {
        const out: {kind: "lock" | "deadbolt"; y: number}[] = [];
        const lock = this.doorPlacement("handle");
        if(lock) out.push({kind: "lock", y: lock.y.get()});
        const deadbolt = this.doorPlacement("lock");
        if(deadbolt) out.push({kind: "deadbolt", y: deadbolt.y.get()});
        return out;
    }

    // every door placement of a kind (one per leaf for a pair with hardware on both leaves).
    private doorPlacements(kind: "handle" | "lock"): MdHardwarePlacement[] {
        return this.list.filter(p => p.target === "door" && p.kind === kind);
    }

    // the door placement projected into the legacy single-value lock preps (cld/dbd/bs). With a
    // placement per leaf, prefer the active leaf — the legacy prep model holds one position per opening.
    private doorPlacement(kind: "handle" | "lock"): MdHardwarePlacement | null {
        const preps = this.doorPlacements(kind);
        return preps.find(p => p.side === "active") ?? preps[0] ?? null;
    }

    // cld/dbd (inches) from a door placement's vertical position; mod when it sits off the standard
    // height above the door bottom — see toLockPreps.
    private verticalPrep(p: MdHardwarePlacement | null, standardAboveBottom: number): SerFrmVar {
        if(!p || !this.doorSpec) return {in: 0};
        const aboveBottom = this.doorSpec.actualHeight.get() - p.y.get();
        const value = aboveBottom - this.doorSpec.undercut.get();
        const overridden = Math.abs(aboveBottom - standardAboveBottom) > POSITION_EPS;
        return {in: value / inches, ...(overridden ? {mod: true} : {})};
    }

    // bs (inches) from a door placement's horizontal position — the crosshair x is to the bore centre,
    // so the backset is the from-edge distance directly; mod when it sits off the standard backset.
    private backsetPrep(p: MdHardwarePlacement | null): SerFrmVar {
        if(!p || !this.doorSpec) return {in: 0};
        const doorWidth = p.side === "inactive" ? this.doorSpec.inActiveDoorWidth.get() : this.doorSpec.activeDoorWidth.get();
        const fromEdge = p.alignToX === "left" ? p.x.get() : doorWidth - p.x.get();
        const overridden = Math.abs(fromEdge - STANDARD_BACKSET) > POSITION_EPS;
        return {in: fromEdge / inches, ...(overridden ? {mod: true} : {})};
    }

    // rebuild from a stored Design. The stored placements replace fromOpening's
    // auto-placement, so this builds fresh (no app / auto-place needed). The load path then
    // calls reconcileToGroup to filter the list to the opening's current hardware group and
    // auto-place any missing primaries (the copy-not-assign rule).
    static fromDesign(design: HardwareDesign, opening: Opening, hardwareGroup: HardwareGroupWithContent | null, unitSystem: UnitSystem, frameSpec: MdFrameSpec, doorSpec: MdDoorSpec): MdHardware {
        const hardware = new MdHardware();
        hardware.attach(frameSpec, doorSpec);
        hardware.setItems(hardwareGroup);

        hardware.hinges = MdHinges.fromDesign(design.hinges, opening, hardwareGroup, unitSystem, frameSpec, doorSpec);
        hardware.watchHinges();

        // a cosmetic item (a sweep, a kick plate) is drawn by the leaf at a fixed spot, so a
        // door placement stored for one before it became cosmetic is dropped.
        const cosmetic = new Set(hardware.items.filter(isCosmetic).map(i => i.product));
        hardware.list = design.list.filter(p => !(p.target === "door" && cosmetic.has(p.productId))).map(p => {
            const x = new FrmVar(`hardware.${p.productId}.${p.target}.${p.side}.x`, 0);
            restoreFrmVar(x, p.x);
            const y = new FrmVar(`hardware.${p.productId}.${p.target}.${p.side}.y`, 0);
            restoreFrmVar(y, p.y);

            return {
                id: p.id,
                kind: p.kind,
                target: p.target,
                side: p.side,
                productId: p.productId,
                stickId: p.stickId,
                alignToX: p.alignToX,
                alignToY: p.alignToY,
                // a design from before the anchors linked placements to each other by id;
                // every such link was a 161 link.
                linkedTo: isPrepKind(p.linkedTo) ? p.linkedTo : p.linkedTo ? "161" : null,
                x,
                y,
            };
        });

        if(design.prepAnchors) {
            restorePrepAnchors(hardware.prepAnchors.active, design.prepAnchors.active);
            restorePrepAnchors(hardware.prepAnchors.inactive, design.prepAnchors.inactive);
        } else {
            hardware.anchorsFromLegacyDesign();
        }

        hardware.listChanged.emit("load");

        return hardware;
    }

    // a design from before the anchors stored the primary handle's and deadbolt's positions
    // directly: read each leaf's 161 off its handle and its DB off its deadbolt (off-standard
    // => modified) and link them to it.
    private anchorsFromLegacyDesign() {
        for(const side of ["active", "inactive"] as const) {
            for(const [kind, placementKind] of [["161", "handle"], ["db", "lock"]] as const) {
                const p = this.list.find(q => q.target === "door" && q.kind === placementKind && q.side === side);
                if(!p) continue;

                const leaf = this.leafFor(side);
                const values = anchorFromPosition(leaf, {x: p.x.get(), y: p.y.get()});
                const std = standardAnchor(kind, leaf);
                const anchor = this.prepAnchors[side][kind];
                if(Math.abs(values.aboveFloor - std.aboveFloor) > POSITION_EPS) anchor.aboveFloor.restore(values.aboveFloor, true);
                if(Math.abs(values.fromStrike - std.fromStrike) > POSITION_EPS) anchor.fromStrike.restore(values.fromStrike, true);
                p.linkedTo = kind;
            }
        }
    }
}

// the door's center lock (the main latching lock at ~40"): the first item matching, in priority
// order, a handleset, an exit device, exit trim, or a lockset. The deadbolt (~48") is found separately
// via isDeadboltItem, so it isn't included here.
function getCenterLock(input: HardwareItem[]): HardwareItem | null {
    return firstByCategoryPriority(input, ["handlesets", "exit devices", "exit trim", "locksets"]);
}

// pick the best-match item for a set of categories given in priority order: the
// first category with any (non-archived) matching item wins, and its first match
// is returned. A category matches if it's a case-insensitive substring of any of
// the item's category / category2 / category3 fields.
function firstByCategoryPriority(input: HardwareItem[], categories: string[]): HardwareItem | null {
    for(const category of categories) {
        const key = category.toLowerCase();
        const match = input.find(item => !item.archived && [item.category, item.category2, item.category3]
            .some(c => !!c && c.toLowerCase().includes(key)));
        if(match) return match;
    }

    return null;
}

function makePlacement(productId: number, target: "door" | "frame", side: "active" | "inactive" | null): MdHardwarePlacement {
    return {
        // unique per product + door/frame + leaf so an item with a door-active,
        // door-inactive and frame crosshair gets three distinct entries.
        id: `hardware:${productId}:${target}:${side ?? "common"}`,
        kind: "generic",
        target,
        // frame preps aren't per-leaf; default the (unused) side to "active".
        side: side ?? "active",
        productId,

        alignToX: "left",
        alignToY: "top",

        // x/y start at 0 and unmodified: the crosshair Thing centres them lazily on
        // first draw and tracks the rect until the user drags or edits a dimension.
        x: new FrmVar(`hardware.${productId}.${target}.${side ?? "common"}.x`, 0),
        y: new FrmVar(`hardware.${productId}.${target}.${side ?? "common"}.y`, 0),
    };
}

// a placement dropped by reconcileToGroup because its product left the opening's hardware
// group. The product is gone from the group, so only its id (+ what it was) is known.
export type DroppedPlacement = {
    productId: number;
    kind: MdHardwarePlacement["kind"];
    target: MdHardwarePlacement["target"];
};

// a primary auto-placed by reconcileToGroup because the group has it but the loaded design
// didn't. The product is in the group, so its name is resolvable for the review dialog.
export type AddedPrimary = {
    productId: number;
    kind: "handle" | "lock";
};

// what changed when a loaded hardware elevation was reconciled against the opening's current
// hardware group — surfaced in the load-time review dialog. `changed` is false when the
// design already matched the group (nothing dropped or added).
export type HardwareReconcileReport = {
    changed: boolean;
    droppedPlacements: DroppedPlacement[];
    addedPrimaries: AddedPrimary[];
    droppedHingeOverrides: number[];   // product ids of cleared hinge overrides
};

export type MdHardwarePlacement = {
    id: string; // stable per crosshair — e.g. "primaryHandle" or "hardware:{productId}:{target}:{side}"
    kind: "handle" | "lock" | "generic";
    target: "door" | "frame"; // "door" rect is the door leaf minus overcut and undercut. "frame" rect is the jamb/head/sill stick area.
    side: "active" | "inactive";
    productId: number;

    stickId?: string; // for a frame crosshair: the id of the jamb/head/sill StickMaterial it's mounted on

    // live position of the crosshair centre as an offset from the target rect's
    // top-left: x from the left edge, y from the top. Seeded lazily (centred) by
    // HardwareCrosshairThing on first draw; userSet by dragging the marker or
    // editing any of its four edge dimensions.
    x: FrmVar;
    y: FrmVar;

    alignToX: "left" | "right" | "both"
    alignToY: "top" | "bottom" | "both"

    // the prep this door placement sits on and moves with: the leaf's 161 or DB anchor (see
    // MdHardware.prepAnchors) or the closer band. Set by rule on add (see prepLinkFor) or by
    // the ribbon's "Linked to"; null (free) when the user picks "None".
    linkedTo?: PrepKind | null;
}

// a leaf's 161 / DB anchor as live vars (see MdHardware.prepAnchors). An unmodified var
// means the standard location, read at use (standardAnchor), so its stored value is ignored.
export type Anchor = {aboveFloor: FrmVar; fromStrike: FrmVar};

function makeAnchors(side: "active" | "inactive"): Record<AnchorKind, Anchor> {
    const make = (kind: AnchorKind): Anchor => ({
        aboveFloor: new FrmVar(`hardware.${kind}.${side}.aboveFloor`, 0),
        fromStrike: new FrmVar(`hardware.${kind}.${side}.fromStrike`, 0),
    });
    return {"161": make("161"), db: make("db")};
}

function serAnchor(a: Anchor): SerAnchor {
    return {aboveFloor: serFrmVar(a.aboveFloor), fromStrike: serFrmVar(a.fromStrike)};
}

function serPrepAnchors(a: Record<AnchorKind, Anchor>): SerPrepAnchors {
    return {"161": serAnchor(a["161"]), db: serAnchor(a.db)};
}

function restoreAnchor(a: Anchor, s: SerAnchor) {
    restoreFrmVar(a.aboveFloor, s.aboveFloor);
    restoreFrmVar(a.fromStrike, s.fromStrike);
}

function restorePrepAnchors(a: Record<AnchorKind, Anchor>, s: SerPrepAnchors) {
    restoreAnchor(a["161"], s["161"]);
    restoreAnchor(a.db, s.db);
}